import { Reducer } from "redux";
import { camelizeKeys } from "humps";
import { CALL_API } from "../helpers/middleware/api";
import createRequestTypes from "../helpers/createRequestTypes";
import {
  HooplaReceiptListElement,
  HooplaReceiptPurchase,
  HooplaReceiptTicket,
  HooplaEvent,
  HooplaTicketType,
  PaginationMeta,
} from "types";

const FETCH_RECEIPTS = createRequestTypes("FETCH_RECEIPTS");
export const fetchReceipts = (offset: number) => ({
  [CALL_API]: {
    types: FETCH_RECEIPTS,
    endpoint: `/api/v2.0/users/purchases?offset=${offset}`,
  },
});

const FETCH_RECEIPT = createRequestTypes("FETCH_RECEIPT");
export const fetchReceipt = (purchaseId: string) => ({
  [CALL_API]: {
    types: FETCH_RECEIPT,
    endpoint: `/api/v2.0/users/purchases/${purchaseId}`,
  },
});

export interface ReceiptsState {
  receipts: {
    fetching: boolean;
    fetched: boolean;
    list: HooplaReceiptListElement[];
    error: boolean;
    pagination: PaginationMeta;
  };
  receipt: {
    fetching: boolean;
    fetched: boolean;
    receipt: HooplaReceiptPurchase;
    error: boolean;
  };
}

const initialState: ReceiptsState = {
  receipts: {
    fetching: false,
    fetched: false,
    list: [],
    error: false,
    pagination: {
      total: 0,
      limit: 10,
      offset: 0,
    },
  },
  receipt: {
    fetching: false,
    fetched: false,
    receipt: null,
    error: false,
  },
};

const reducer: Reducer<ReceiptsState> = (
  state: ReceiptsState = initialState,
  action: any,
): ReceiptsState => {
  switch (action.type) {
    case FETCH_RECEIPTS.REQUEST:
      return { ...state, receipts: { ...state.receipts, fetching: true, fetched: false } };
    case FETCH_RECEIPTS.SUCCESS:
      return {
        ...state,
        receipts: {
          error: false,
          fetching: false,
          fetched: true,
          list: action.response.receipts.map(
            (purchase: any): HooplaReceiptListElement => ({
              purchaseId: purchase.purchase_id,
              totalAmount: purchase.total_amount,
              finalized: purchase.finalized,
              info: purchase.events.map((event: any) => event.name).join(", "),
            }),
          ),
          pagination: {
            total: action.response.total_count,
            limit: action.response.limit,
            offset: action.response.offset,
          },
        },
      };
    case FETCH_RECEIPTS.FAILURE:
      return {
        ...state,
        receipts: { ...state.receipts, fetching: false, fetched: true, error: true },
      };

    case FETCH_RECEIPT.REQUEST:
      return { ...state, receipt: { ...state.receipt, fetching: true, fetched: false } };
    case FETCH_RECEIPT.SUCCESS:
      return {
        ...state,
        receipt: {
          error: false,
          fetching: false,
          fetched: true,
          receipt: {
            purchaseId: action.response.purchase_id,
            finalized: action.response.finalized,
            totalAmount: action.response.total_amount,
            totalVat: action.response.total_vat,
            orderRef: action.response.order_ref,
            checkoutMethod: action.response.checkout_method,
            tickets: action.response.tickets.map(
              (ticket: any): HooplaReceiptTicket => ({
                ticketId: ticket.ticket_id,
                seatName: ticket.seat_name,
                totalAmount: ticket.total_amount,
                totalVat: ticket.total_vat,
                vatFactor: ticket.vat_factor,
                event: camelizeKeys(ticket.event as object) as HooplaEvent,
                organization: ticket.organization,
                ticketType: camelizeKeys(ticket.ticket_type as object) as HooplaTicketType,
              }),
            ),
          },
        },
      };
    case FETCH_RECEIPT.FAILURE:
      return {
        ...state,
        receipt: { ...state.receipt, fetching: false, fetched: true, error: true },
      };

    default:
      return state;
  }
};

export default reducer;
